import { useMemo, useRef, useState, type ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Upload } from "lucide-react";
import { downloadExcelTemplate, readExcelRows } from "../../utils/excel";
import {
  headersOf,
  validateRows,
  type ImportSpec,
  type ParsedRow,
} from "@event-planner/shared/imports";
import {
  BulkImportResultTable,
  type BulkImportResultBucket,
} from "../BulkImportResultTable";

/**
 * Generic resolve-and-propose import dialog. Owns the 5-phase state
 * machine (picking → loading → reviewing → writing → done) and the
 * pieces that are identical across every entity type:
 *
 *   - Excel file pick + parse + format-validate (host provides the spec)
 *   - Loading-phase spinner during the resolve round-trip
 *   - Bulk include actions in the reviewing phase (accept all / skip all)
 *   - Per-row container for the host-rendered ProposalCard
 *   - Done-phase BulkImportResultTable (host builds the buckets)
 *
 * Per-entity rendering, status enums, and result bucketing are injected
 * by the host. The dialog is fully generic over `TParsed` (the parsed
 * row shape from the spec), `TResolveRow` (the per-row resolve callable
 * result), and `TWriteResult` (the entire write callable response — the
 * host pulls per-row results out of it however it needs to).
 *
 * Phase B's `ExcelLocationImportDialog` was the prototype this is
 * extracted from; refactoring that dialog to use this component should
 * be behavior-preserving. Phase C's other entity dialogs (Audiences,
 * Sessions, Travel, Attendees, Users) all wire through this same shell.
 */

export interface ProposalRow<TParsed, TResolveRow> {
  /** 1-based source row number in the workbook (header is row 1). */
  lineNumber: number;
  /** Original parsed row (for the write-phase resubmit). */
  parsed: ParsedRow<TParsed>;
  /** Resolution result. Null when the row was format-invalid (skipped resolve). */
  resolution: TResolveRow | null;
  /** User's include-this-row toggle. Defaults derived per host policy. */
  include: boolean;
  /** True when the include toggle is locked off (not user-toggleable). */
  includeLocked: boolean;
}

export interface ResolveAndImportDialogProps<TParsed, TResolveRow, TWriteResult> {
  open: boolean;
  onClose: () => void;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;

  // ── Copy ──
  /** Title text shown in the dialog header — e.g. "Import Locations from Excel". */
  title: string;
  /** Singular + plural form of the entity. Used in confirm-button copy + spinners. */
  entityNoun: { singular: string; plural: string };
  /** Help text shown above the file picker in the picking phase. */
  pickerHelpText: ReactNode;
  /** Slug used for `data-testid` attributes (e.g. "location" → "excel-location-import-dialog"). */
  testIdSlug: string;

  // ── Spec ──
  spec: ImportSpec<Record<string, string | undefined>, TParsed>;
  /** Filename for the downloaded template (e.g. "location-import-template.xlsx"). */
  templateFileName: string;
  /** Sheet name inside the downloaded template. */
  sheetName: string;

  // ── Async actions ──
  /**
   * Resolve a batch of parsed rows server-side. Returns one entry per input
   * row in the same order. Throws → dialog falls back to the picking phase
   * with the thrown error surfaced.
   */
  resolveRows: (rows: TParsed[]) => Promise<TResolveRow[]>;
  /**
   * Optional workbook-level preparation hook. Runs after the file is chosen
   * and before this dialog parses/resolves its own sheet. Hosts use this for
   * same-workbook dependencies, e.g. Agenda import applying the Session Types
   * sheet before resolving Sessions.
   */
  prepareFile?: (file: File) => Promise<void>;
  /**
   * Write the user-included rows. The host owns the result-merge logic;
   * the response shape is opaque to this dialog and just passed back into
   * `buildResultBuckets`. Optional when `writeProposals` is provided.
   */
  writeRows?: (rows: TParsed[]) => Promise<TWriteResult>;

  /**
   * Optional alternative to `writeRows` that receives the full
   * proposals (parsed row + resolution + include state) so the host can
   * dispatch per-row based on the resolution status — e.g. send `new`
   * rows through createContact and matched-with-diff rows through
   * updateContact. When provided, the dialog calls this instead of
   * `writeRows`.
   */
  writeProposals?: (
    proposals: ProposalRow<TParsed, TResolveRow>[],
  ) => Promise<TWriteResult>;

  // ── Per-row UX hooks ──
  /**
   * Default include + locked state per row. Called once per resolution
   * (or with `null` for format-invalid rows). Hosts typically:
   *   - default include=true on rows that are clean ('new')
   *   - default include=false + locked on every other status (no clobber)
   */
  defaultIncludeForResolution: (
    resolution: TResolveRow | null,
  ) => { include: boolean; locked: boolean };

  /**
   * Render the per-row review card. Host owns the entity-specific layout
   * (Apple-canonical address for locations, per-email proposal chips for
   * audiences, etc.).
   */
  renderProposalCard: (proposal: ProposalRow<TParsed, TResolveRow>, onToggle: () => void) => ReactNode;

  // ── Done-phase bucketing ──
  /**
   * Build the result buckets shown in the done phase. Receives the write
   * response (when there were included rows) and the skipped proposals.
   */
  buildResultBuckets: (args: {
    writeResult: TWriteResult | null;
    /** Proposals the user explicitly excluded or which were locked off. */
    skippedProposals: ProposalRow<TParsed, TResolveRow>[];
    /** Proposals that were included in the write call. */
    includedProposals: ProposalRow<TParsed, TResolveRow>[];
  }) => BulkImportResultBucket[];

  /**
   * Predicate against the write response: true when the import wrote at
   * least one doc. Drives the `onImported` callback on close.
   */
  importedAnythingFromResult: (writeResult: TWriteResult) => boolean;
}

type Phase = "picking" | "loading" | "reviewing" | "writing" | "done";

export function ResolveAndImportDialog<TParsed, TResolveRow, TWriteResult>({
  open,
  onClose,
  onImported,
  title,
  entityNoun,
  pickerHelpText,
  testIdSlug,
  spec,
  templateFileName,
  sheetName,
  resolveRows,
  prepareFile,
  writeRows,
  writeProposals,
  defaultIncludeForResolution,
  renderProposalCard,
  buildResultBuckets,
  importedAnythingFromResult,
}: ResolveAndImportDialogProps<TParsed, TResolveRow, TWriteResult>) {
  const [phase, setPhase] = useState<Phase>("picking");
  const [fileName, setFileName] = useState<string | null>(null);
  const [proposals, setProposals] = useState<ProposalRow<TParsed, TResolveRow>[]>(
    [],
  );
  const [writeResult, setWriteResult] = useState<TWriteResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importedAnythingRef = useRef(false);

  const includedCount = useMemo(
    () => proposals.filter((p) => p.include).length,
    [proposals],
  );

  function reset() {
    setPhase("picking");
    setFileName(null);
    setProposals([]);
    setWriteResult(null);
    setParseError(null);
    setResolveError(null);
    setImportError(null);
  }

  function handleClose() {
    if (phase === "loading" || phase === "writing") return;
    if (importedAnythingRef.current && onImported) onImported();
    importedAnythingRef.current = false;
    reset();
    onClose();
  }

  async function handleFile(file: File) {
    setParseError(null);
    setResolveError(null);
    setFileName(file.name);
    setPhase("loading");

    let parsed: ParsedRow<TParsed>[];
    try {
      if (prepareFile) await prepareFile(file);
      const { headers, rows: rawRows } = await readExcelRows<
        Record<string, string | undefined>
      >(file, { sheetName: spec.sheetName });
      assertHeadersMatch(headers, headersOf(spec), spec.sheetName);
      parsed = validateRows(spec, rawRows);
    } catch (err) {
      setParseError(
        err instanceof Error
          ? `Failed to read file: ${err.message}`
          : "Failed to read file.",
      );
      setPhase("picking");
      return;
    }

    const pending = parsed.filter((r) => r.status === "pending");
    const invalid = parsed.filter((r) => r.status === "invalid");

    // Short-circuit: every row is format-invalid → skip the resolve
    // round-trip and jump to reviewing so the user sees what's wrong.
    if (pending.length === 0) {
      const next: ProposalRow<TParsed, TResolveRow>[] = invalid.map((row) => {
        const def = defaultIncludeForResolution(null);
        return {
          lineNumber: row.lineNumber,
          parsed: row,
          resolution: null,
          include: def.include,
          includeLocked: def.locked,
        };
      });
      setProposals(next);
      setPhase("reviewing");
      return;
    }

    try {
      const resolutions = await resolveRows(pending.map((r) => r.data));

      // pending[i] corresponds to resolutions[i] — preserve order.
      const proposalRows: ProposalRow<TParsed, TResolveRow>[] = pending.map(
        (row, i) => {
          const resolution = resolutions[i];
          const def = defaultIncludeForResolution(resolution);
          return {
            lineNumber: row.lineNumber,
            parsed: row,
            resolution,
            include: def.include,
            includeLocked: def.locked,
          };
        },
      );

      // Surface format-invalid rows alongside the resolved ones in line-order.
      proposalRows.push(
        ...invalid.map<ProposalRow<TParsed, TResolveRow>>((row) => {
          const def = defaultIncludeForResolution(null);
          return {
            lineNumber: row.lineNumber,
            parsed: row,
            resolution: null,
            include: def.include,
            includeLocked: def.locked,
          };
        }),
      );

      proposalRows.sort((a, b) => a.lineNumber - b.lineNumber);
      setProposals(proposalRows);
      setPhase("reviewing");
    } catch (err) {
      setResolveError(
        err instanceof Error
          ? `Failed to resolve: ${err.message}`
          : "Failed to resolve.",
      );
      setPhase("picking");
    }
  }

  function toggleInclude(lineNumber: number) {
    setProposals((prev) =>
      prev.map((p) =>
        p.lineNumber === lineNumber && !p.includeLocked
          ? { ...p, include: !p.include }
          : p,
      ),
    );
  }

  async function handleImport() {
    setPhase("writing");
    setImportError(null);

    const included = proposals.filter((p) => p.include);
    const skipped = proposals.filter((p) => !p.include);

    if (included.length === 0) {
      setWriteResult(null);
      importedAnythingRef.current = false;
      setPhase("done");
      return;
    }

    try {
      const response = writeProposals
        ? await writeProposals(included)
        : await writeRows!(included.map((p) => p.parsed.data));
      setWriteResult(response);
      importedAnythingRef.current = importedAnythingFromResult(response);
      setPhase("done");
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : "Bulk import failed.",
      );
      setPhase("reviewing");
    }

    // Skipped reference suppresses the "unused" lint until the host
    // bucket-builder reads it via the rendered done phase.
    void skipped;
  }

  const buckets = useMemo<BulkImportResultBucket[]>(() => {
    if (phase !== "done") return [];
    const included = proposals.filter((p) => p.include);
    const skipped = proposals.filter((p) => !p.include);
    return buildResultBuckets({
      writeResult,
      includedProposals: included,
      skippedProposals: skipped,
    });
  }, [phase, proposals, writeResult, buildResultBuckets]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid={`excel-${testIdSlug}-import-dialog`}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {phase === "picking" && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {pickerHelpText}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                onClick={async () =>
                  downloadExcelTemplate(templateFileName, {
                    headers: headersOf(spec),
                    exampleRows: [
                      spec.example as Record<string, string | number | null>,
                    ],
                    sheetName,
                  })
                }
                data-testid={`excel-${testIdSlug}-template-button`}
              >
                Download Template
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload size={16} />}
                component="label"
                data-testid={`excel-${testIdSlug}-upload-button`}
              >
                Choose File
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFile(file);
                  }}
                />
              </Button>
            </Stack>
            {parseError && <Alert severity="error">{parseError}</Alert>}
            {resolveError && <Alert severity="error">{resolveError}</Alert>}
          </Stack>
        )}

        {phase === "loading" && (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2">Resolving...</Typography>
            <Typography variant="caption" color="text.secondary">
              {fileName}
            </Typography>
          </Stack>
        )}

        {phase === "reviewing" && (
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="body2">
                <strong>{fileName}</strong>: review {proposals.length} row
                {proposals.length === 1 ? "" : "s"}
              </Typography>
              <Link
                component="button"
                onClick={reset}
                sx={{ ml: "auto", fontSize: "0.85rem" }}
              >
                Choose a different file
              </Link>
            </Stack>
            {importError && <Alert severity="error">{importError}</Alert>}
            <Stack spacing={1}>
              {proposals.map((p) => (
                <div key={p.lineNumber}>
                  {renderProposalCard(p, () => toggleInclude(p.lineNumber))}
                </div>
              ))}
            </Stack>
          </Stack>
        )}

        {phase === "writing" && (
          <Stack spacing={2} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2">
              Importing {includedCount}{" "}
              {includedCount === 1 ? entityNoun.singular : entityNoun.plural}...
            </Typography>
          </Stack>
        )}

        {phase === "done" && <BulkImportResultTable buckets={buckets} />}
      </DialogContent>
      <DialogActions>
        {phase === "picking" && <Button onClick={handleClose}>Cancel</Button>}
        {phase === "reviewing" && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={includedCount === 0}
              data-testid={`excel-${testIdSlug}-import-confirm`}
            >
              Import {includedCount}{" "}
              {includedCount === 1 ? entityNoun.singular : entityNoun.plural}
            </Button>
          </>
        )}
        {phase === "done" && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function assertHeadersMatch(
  actualHeaders: string[],
  expectedHeaders: string[],
  sheetName: string,
): void {
  const actual = actualHeaders.map((h) => h.trim());
  const expected = expectedHeaders.map((h) => h.trim());
  const exact =
    actual.length === expected.length &&
    actual.every((header, index) => header === expected[index]);

  if (exact) return;

  throw new Error(
    `Sheet "${sheetName}" headers do not match the import template. ` +
      `Expected exactly: ${expected.join(", ")}. ` +
      `Found: ${actual.filter(Boolean).join(", ") || "(none)"}.`,
  );
}
